



































































































import Vue, { PropType } from 'vue'
import { mapGetters } from 'vuex'
import DataProviderOverlay from '@/components/DataProviderOverlay.vue'
import { cloneDeep, differenceWith, isEqual } from '@/utility/helpers'
import Person from '@/types/Person.interface'
import Button from '@/types/components/Button.interface'
import Group from '@/types/Group.interface'
import PersonComposeMessage from '@/components/PersonComposeMessage.vue'
import PersonAddresses from '@/components/PersonAddresses.vue'
import PersonExtensions from '@/components/PersonExtensions.vue'
import PersonGroups from '@/components/PersonGroups.vue'
import PersonMoreInfo from '@/components/PersonMoreInfo.vue'
import PersonNames from '@/components/PersonNames.vue'
import PersonPhones from '@/components/PersonPhones.vue'
import extensionsMixin from '@/mixins/extension-options-mixin'
import PersonInfo from '@/components/PersonInfo.vue'
import PersonInvoices from '@/components/PersonInvoices.vue'
import PersonMedia from '@/components/PersonMedia.vue'
import PersonNotes from '@/components/PersonNotes.vue'
import PersonShowItem from '@/components/PersonShowItem.vue'
import PersonShowTitle from '@/components/PersonShowTitle.vue'
import printer from '@/plugins/printer'
import TabPanel from '@/components/TabPanel.vue'
import UserProfile from '@/components/UserProfile.vue'
import User from '@/types/User.interface'
export default Vue.extend({
    mixins: [extensionsMixin],
    components: {
        DataProviderOverlay,
        PersonAddresses,
        PersonComposeMessage,
        PersonExtensions,
        PersonGroups,
        PersonInfo,
        PersonInvoices,
        PersonMedia,
        PersonMoreInfo,
        PersonNames,
        PersonNotes,
        PersonPhones,
        PersonShowItem,
        PersonShowTitle,
        TabPanel,
        UserProfile
    },
    props: {
        //  Determines if the 'back' button is shown in the profile
        back: {
            type: Boolean,
            default: false
        },
        //  Overrides the default back button tooltip
        backTooltip: {
            type: String,
            default: null
        },
        //  The person bound to the component
        person: {
            type: Object as PropType<Person>,
            required: true
        }
    },
    data: () => ({
        //  The current width of the person profile
        currentWidth: 500,
        //  Indicates of the 'temp' clone has been altered and is no longer equal to the 'person'
        edited: false,
        //  Indicates that the component is in editing mode
        editing: false,
        //  Object keeping information regarding which profile sections are expanded
        fieldsExpanded: {
            addy: false,
            autoresponder: false,
            customField: false,
            group: false,
            moreInfo: false,
            name: false,
            phones: false
        },
        //  Indicates if the person is currently being saved (used for loading spinner)
        saving: false,
        //  A deep clone of the 'person' (used for editing)
        temp: null as Person,
        //  The profile tabs
        tabItems: null
    }),
    mounted() {
        //  Set our tabs
        this.setTabs(this.person)

        //  Clone the person bound to the component (we use the clone to track changes)
        this.setTemp(this.person)

        //  Set the component width (we use this to adjust text for small widths)
        this.setWidth()
    },
    computed: {
        ...mapGetters(['extensionOptions', 'hasInvoices', 'tenant', 'users']),
        addressButtons(): Button[] {
            //  Buttons displayed on the right side of the 'Custom Fields' section title
            return [
                {
                    icon: this.fieldsExpanded.addy ? 'mdi-menu-up' : 'mdi-menu-down',
                    tooltipLeft: true,
                    callback: () =>
                        (this.fieldsExpanded.addy = !this.fieldsExpanded.addy),
                    tooltip: 'Expand Address Details'
                }
            ]
        },
        areFieldsExpanded(): boolean {
            //  Bool value true if any attributes in fieldsExpanded are true and false otherwise.
            const areExpanded = Object.values(this.fieldsExpanded).some(
                (val) => val === true
            )

            //  Returns this value for show all / hide all button
            return areExpanded
        },
        autoresponderButtons(): Button[] {
            return [
                {
                    icon: this.fieldsExpanded.autoresponder
                        ? 'mdi-menu-up'
                        : 'mdi-menu-down',
                    tooltipLeft: true,
                    callback: () =>
                        (this.fieldsExpanded.autoresponder =
                            !this.fieldsExpanded.autoresponder),
                    tooltip: 'Expand Autoresponders Details'
                }
            ]
        },
        //  Buttons displayed to the right of the person title
        buttonsRight(): Button[] {
            //  Check if we're on an extra small screen
            const xsScreen = this.currentWidth < 500

            //  Set text for desktop (On larger screens we return full text)
            let text = this.editing || this.edited ? 'Save Changes' : 'Edit'

            //  If we're on a xsSmall (ie mobile device) screen, we return a shorter text to save space
            if (xsScreen) text = this.editing || this.edited ? 'Save' : 'Edit'

            //  Set the icon (if we're editing, return a 'save' icon, else return an 'edit' icon)
            const icon = this.editing ? 'mdi-content-save' : 'mdi-pencil'

            //  Determine if the 'print' buttons will be displayed (we hide them on mobile)
            const hidePrint =
                this.$vuetify.breakpoint.xsOnly ||
                this.$store.getters.browser === 'Safari'

            //  Return the buttons array
            return [
                {
                    text: text,
                    color: 'primary',
                    icon: this.$vuetify.breakpoint.xsOnly && icon,
                    loading: this.saving,
                    class: 'px-1',
                    small: true,
                    hidden: this.selectedUser ? true : false,
                    callback: () => {
                        //  If we are 'editing', we need to save changes (In editing mode, the button acts as a 'save' button
                        if (this.editing) {
                            //  Grab our errors
                            const errors = this.errors.items.filter(
                                (x) => x.scope === 'person'
                            )
                            //  Validate our inputs
                            const valid = !errors.length

                            //  If invalid, pop an error
                            if (!valid) return this.displayErrors()

                            //  Update the person
                            this.personUpdate()

                            //  Disable 'editing' mode
                            this.editing = false
                        }

                        //  Enable 'editing' mode (If we are NOT in editing mode, the button acts as an 'enable' button)
                        else {
                            //  Enable editing mode
                            this.editing = true

                            //  Focus on the first item in PersonInfo
                            this.$refs.personInfo.focusOnFirst()
                        }
                    }
                },
                {
                    tooltip: 'Print contact',
                    icon: 'mdi-printer',
                    loading: false,
                    hidden: hidePrint,
                    color: 'grey lighten-1',
                    callback: () => printer.printCustomer(this.person)
                },
                {
                    tooltip: 'Add to print queue',
                    icon: 'mdi-note-plus',
                    loading: false,
                    hidden: hidePrint,
                    color: 'grey lighten-1',
                    callback: () => {
                        //  Add the person to the print queue
                        this.$store.commit('printQueueAdd', this.person)

                        //  Notify the user that the person was added
                        this.$store.commit('appNotificationBarSet', {
                            message: 'Added to print queue'
                        })
                    }
                }
            ]
        },
        //  Buttons displayed to the left of the person title
        buttonsLeft(): Button[] {
            //  Check if we're on an extra small screen
            const xsScreen = this.currentWidth < 500

            //  Set text for desktop (On larger screens we return full text)
            let text = this.editing && 'Undo Changes'

            //  If we're on a xsSmall (ie mobile device) screen, we return a shorter text to save space
            if (xsScreen) text = this.editing && 'Undo'

            //  Our back icon depends on screen size
            const backIcon = this.$vuetify.breakpoint.xsOnly
                ? 'mdi-chevron-double-left'
                : 'mdi-chevron-double-right'

            //  Our undo button depends on edit mode, and if a user exists
            const hideUndo = !this.editing || this.saving || this.selectedUser !== null

            return [
                {
                    tooltip: this.backTooltip,
                    icon: backIcon,
                    color: 'grey lighten-1',
                    small: false,
                    hidden: !this.back,
                    callback: () => this.$emit('back')
                },
                {
                    text: text,
                    icon: this.$vuetify.breakpoint.xsOnly && 'mdi-content-save-off',
                    small: true,
                    class: 'px-1 mt-1',
                    hidden: hideUndo,
                    callback: () => {
                        //  Discard changes
                        this.setTemp(this.person)

                        //  Disable editing mode
                        this.editing = false
                    }
                }
            ]
        },
        //  The buttons displayed in the 'Custom Fields' title
        customFieldsButtons(): Button[] {
            //  Buttons displayed on the right side of the 'Custom Fields' section title
            return [
                {
                    icon: 'mdi-help-circle',
                    tooltipLeft: true,
                    callback: () => this.popCustomFieldsHelpDialog(),
                    tooltip: 'Custom Fields Help'
                },
                {
                    icon: 'mdi-format-annotation-plus',
                    tooltipLeft: true,
                    callback: () => this.$router.push('custom-fields'),
                    tooltip: 'Go to custom fields'
                },
                {
                    icon: 'mdi-plus',
                    tooltipLeft: true,
                    tooltip: 'Add Custom Field',
                    callback: () => this.goToExtensionCreate()
                },
                {
                    icon: this.fieldsExpanded.customField
                        ? 'mdi-menu-up'
                        : 'mdi-menu-down',
                    tooltipLeft: true,
                    callback: () =>
                        (this.fieldsExpanded.customField =
                            !this.fieldsExpanded.customField),
                    tooltip: 'Expand Custom Fields Details'
                }
            ]
        },
        groupsButtons(): Button[] {
            return [
                {
                    icon: 'mdi-help-circle',
                    tooltipLeft: true,
                    callback: () => this.popGroupHelp(),
                    tooltip: 'Groups Help'
                },
                {
                    icon: 'mdi-plus',
                    tooltipLeft: true,
                    tooltip: 'Add to Group',
                    callback: () => this.popAddPersonToGroup()
                },
                {
                    icon: this.fieldsExpanded.group ? 'mdi-menu-up' : 'mdi-menu-down',
                    tooltipLeft: true,
                    callback: () =>
                        (this.fieldsExpanded.group = !this.fieldsExpanded.group),
                    tooltip: 'Expand Groups Details'
                }
            ]
        },
        //  Determine if the component is hosted in the 'Inbox' (we hide the 'message' tab when the profile is in Inbox)
        inbox(): boolean {
            return this.$route.path.includes('inbox')
        },
        //  The buttons displayed in the 'Custom Fields' title
        moreInfoButtons(): Button[] {
            //  Buttons displayed on the right side of the 'Custom Fields' section title
            return [
                {
                    icon: this.fieldsExpanded.moreInfo ? 'mdi-menu-up' : 'mdi-menu-down',
                    tooltipLeft: true,
                    callback: () =>
                        (this.fieldsExpanded.moreInfo = !this.fieldsExpanded.moreInfo),
                    tooltip: 'Expand More Info Details'
                }
            ]
        },
        nameDetailsButtons(): Button[] {
            //  Buttons displayed on the right side of the 'Custom Fields' section title
            return [
                {
                    icon: this.fieldsExpanded.name ? 'mdi-menu-up' : 'mdi-menu-down',
                    tooltipLeft: true,
                    callback: () =>
                        (this.fieldsExpanded.name = !this.fieldsExpanded.name),
                    tooltip: 'Expand Name Details'
                }
            ]
        },
        phonesButtons(): Button[] {
            return [
                {
                    icon: this.fieldsExpanded.phones ? 'mdi-menu-up' : 'mdi-menu-down',
                    tooltipLeft: true,
                    callback: () =>
                        (this.fieldsExpanded.phones = !this.fieldsExpanded.phones),
                    tooltip: 'Expand Phone Details'
                }
            ]
        },
        personHelp(): object {
            //  Check if the tenant is connected to QBO
            const qbo = this.tenant.data_provider === 'QBO'

            //  If the tenant is NOT connected to qbo, return null
            if (!qbo) return null

            //  Else, grab the type of person so we can check if they are bound to QBO
            const type = this.$options.filters.personType(this.person)

            //  If they are NOT a Contact, then just return null
            if (type !== 'Contact') return null

            //  Else, we need to display a help dialog informing why they are a "Contact"
            return {
                title: 'MessageDesk Only Contact',
                body: "This contact is not connected to your current QuickBooks integration. This can happen if an error occurs while creating the contact in MessageDesk, such as the contact's name already existing in QBO."
            }
        },
        //  Return phone number title as Singular or Plural
        phonesTitle(): string {
            //  If a person does NOT exist, return empty string
            if (!this.person) return ''

            //  Grab the person phones
            const phones = this.person.phones

            //  If no phones OR phone length is 1, return Phone Number
            if (!phones || phones.length === 1) return 'Phone Number'

            //  Else, return Phone Numbers
            return 'Phone Numbers'
        },
        //  The icon shown in the person title
        subtitleIcon(): string {
            //  If our person has a user, then we want to get the user icon from the user profile
            if (this.person.user_id) {
                //  If no selected user yet, just return empty for now
                if (!this.selectedUser) return ''

                //  Else, return our role
                return this.$options.filters.userIcon(this.selectedUser)
            }

            //  Else, go for our person icon
            return this.$options.filters.personIcon(this.person)
        },
        //  The user that is attached to the person (only exists if the person is a user)
        selectedUser(): User | null {
            //  If the person does NOT have a user_id, return null
            if (!this.person?.user_id) return null

            //  Else, fetch the user from the local store
            const user = this.users.find((x: User) => x.id === this.person.user_id)

            return user
        },
        teammates(): boolean {
            return this.$route.path.includes('teammates')
        }
    },
    methods: {
        //  Pop a 'confirm' dialog (we need to post unsaved changes if we're navigating away)
        confirmChanges(callback: Function): void {
            //  Grab our errors
            const errors = this.errors.items.filter((x) => x.scope === 'person')

            //  Validate our inputs
            const valid = !errors.length

            //  Get the Vue filters
            const f = this.$options.filters

            //  Get the person's full name
            const name = f.personFullNameOrEmail(this.person)

            //  Assemble the dialog text
            const text = valid
                ? `Do you want to save changes to ${name}?`
                : `There are currently errors in your changes for ${name}.`

            //  Pop the confirm dialog
            this.$store.dispatch('dialogOpen', {
                type: 'CONFIRM',
                title: valid ? 'Save Changes?' : 'Fix Errors?',
                text: text,
                noText: 'Discard Changes',
                yesText: valid ? 'Save Changes' : 'Fix Errors',
                callback: (result: boolean) =>
                    this.onConfirmChanges(result, valid, callback)
            })
        },
        displayErrors(): void {
            //  Grab our errors
            const errors = this.errors.items.filter((x) => x.scope === 'person')

            //  Grab all messages from our errors
            const msgs = errors.map((x) => x.msg)

            //  Create our string container
            const str = msgs.join('\n')

            //  Pop a dialog
            this.$store.dispatch('dialogOpen', {
                type: 'MESSAGE',
                title: 'Fix Errors',
                text: str
            })
        },
        //  Determine is the 'phones' component is displayed
        displayPhones(): boolean {
            //  Only display phones if we are qbo or xero
            return (
                this.tenant.data_provider === 'XERO' ||
                this.tenant.data_provider === 'QBO'
            )
        },
        //  Determine is invoices tab is hidden for a person
        invoicesHidden(person: Person): boolean {
            //  If the person is a user, hide the invoices tab
            if (person && (person.user_id || person.type === 'LEAD')) return true

            //  If the tenant does NOT have invoices, hide the tab
            if (!this.hasInvoices) return true

            //  Else, show the invoices tab
            return false
        },
        async onConfirmChanges(
            result: boolean,
            valid: boolean,
            callback: Function
        ): Promise<void> {
            //  If the user answered 'NO', discard changes
            if (result === false) this.setTemp(this.person)

            //  If this is valid AND the result is true, we can save
            const save = result === true && valid

            //  If this is NOT valid and the result is true, we can fix errors
            const fix = result === true && !valid

            //  If we can save, then save
            if (save) await this.personUpdate()

            //  If we are fixing, then display the errors
            if (fix) return this.displayErrors()

            //  Disabled editing mode
            this.editing = false

            //  If a callback was passed, use it!
            if (callback) callback()
        },
        //  Update the person with changes stored in the 'temp' clone
        async personUpdate(): Promise<void> {
            //  Compare the person with its clone (we'll use this as the sparse update payload)
            const diff = differenceWith(this.temp, this.person)

            //  If no changes are detected, do nothing
            if (Object.keys(diff).length === 0) return

            //  ELse, start he loading spinner
            this.saving = true

            //  Add the person id to the changes (id is requird to make a sparse update)
            diff.id = this.temp.id

            //  Send the request
            await this.$store.dispatch('personsPut', diff)

            //  Stop the loading spinner
            this.saving = false

            // We also set editing to false here as sometime user's change the address and when they click save it doesn't make sense
            // for us to also say save at the top of the profile
            this.editing = false
        },
        //  Pop the Add-to-Group form dialog
        popAddPersonToGroup(): void {
            //  Our persons to send in the form
            const persons = [this.person]

            // We need to expand the group's dropdown if they click the + icon otherwise we can't access personGroups ref in our callback as it's not rendered on screen
            this.fieldsExpanded.group = true

            //  Pop our dialog for adding groups
            this.$store.dispatch('dialogOpen', {
                type: 'APP',
                title: 'Add to Group',
                component: 'add-to-group-form',
                closeable: true,
                cardStyle: { paddingBottom: '20px !important' },
                props: {
                    startStep: 3,
                    persons: persons,
                    push: false
                },
                closeOnSubmit: true,
                callback: (response: Group[]) => {
                    //  Pass our group to our person groups
                    this.$refs.personGroups.addGroup(response)
                }
            })
        },
        //  Pop the custom fields help dialog
        popCustomFieldsHelpDialog(): void {
            //  Grab our system variables
            const learnUrl = process.env.VUE_APP_URL_LEARN + '/en/articles/4527055-how-to-create-and-edit-custom-fields'
            const target = this.$store.getters.isApp ? '_system' : '_blank'

            this.$store.dispatch('dialogOpen', {
                type: 'MESSAGE',
                icon: 'mdi-help-circle',
                color: 'primary',
                title: 'Custom Fields',
                text: `Custom fields make it quick and easy to segment your contacts into actionable groups. These groupings of custom fields can then be texted directly, receive scheduled messages and more. <a href="${learnUrl}" target=${target}>Read the how-to guide to custom fields</a>.`
            })
        },
        popGroupHelp(): void {
            this.$store.dispatch('dialogOpen', {
                type: 'MESSAGE',
                title: 'Assigned Group',
                text: `This is where you'll see all groups that this contact is a member of. You can add or remove the contact to groups here as well.`,
                icon: 'mdi-account-multiple',
                color: 'primary'
            })
        },
        //  Update the profile tabs
        setTabs(person: Person): void {
            this.tabItems = [
                {
                    text: 'Profile',
                    icon: 'mdi-account'
                },
                {
                    text: 'Uploaded',
                    icon: 'mdi-folder-multiple-image'
                },
                {
                    text: 'Attachments',
                    icon: 'mdi-message-image'
                },
                {
                    text: 'Invoices',
                    icon: 'mdi-receipt',
                    hidden: this.invoicesHidden(person)
                },
                {
                    text: 'Notes',
                    icon: 'mdi-file-document'
                },
                {
                    text: 'Message',
                    icon: 'bi-chat-left-dots-fill',
                    hidden: this.inbox || this.teammates
                }
            ]
        },
        //  Reset our 'temp' clone (this occurs when a new person is loaded, an update is made, or changes are discarded)
        setTemp(person: Person): void {
            //  Clone the selected person
            this.temp = cloneDeep(person)

            //  Reset the 'edited' property
            this.edited = false
        },
        //  Set the current width of the person profile (we use this for shrinking display text)
        setWidth(): void {
            //  We use $nextTick to be sure the element is rendered before we measure its width
            this.$nextTick(() => {
                //  Get the element from the DOM
                const el = this.$refs.tabPanel

                //  Get the elements width
                const width = el.$el.clientWidth

                //  Set the current width
                this.currentWidth = width
            })
        },
        showAllToggle(): void {
            //  Bool variable cond holds whether any of the variables within fieldsExpanded object are true
            const cond = Object.values(this.fieldsExpanded).some((val) => val === true)

            //  Set all fieldExpanded attributes to false (hide all) or true (indicates show all)
            for (let key in this.fieldsExpanded) {
                this.fieldsExpanded[key] = !cond
            }
        }
    },
    watch: {
        person(person: Person | null): void {
            //  If the person is null, do nothing
            if (!person) return

            //  Set the tabs for this new person
            this.setTabs(person)

            //  Reset the temp clone, since the person has changed
            this.setTemp(person)

            //  Grab the media for the person
            this.$store.dispatch('personSelectedMediaPaginate', person.id)
        },
        temp: {
            deep: true,
            handler(temp: Person) {
                //  Check if the temp clone is equal to the selected person
                const equal = isEqual(temp, this.person)

                // Debugging
                const diff = differenceWith(temp, this.person)

                //  If the person and temp are equal, remove the 'save changes' button
                if (equal) return (this.edited = false)

                //  Else, show the 'save changes' button
                this.editing = true
                this.edited = true
            }
        }
    }
})
